import React, { createContext, useContext, useState, useEffect } from 'react';
import { collection, query, where, onSnapshot, Timestamp } from 'firebase/firestore';
import { db } from '../lib/firebase/config';
import { useAuth } from './AuthContext';

export interface ChatWindow {
  chatId: string;
  chatData: any;
  isMinimized: boolean;
  unreadCount: number;
  lastMessageTimestamp?: Timestamp;
}

interface ChatBarContextType {
  openChats: ChatWindow[];
  openChat: (chatId: string, chatData: any) => void;
  closeChat: (chatId: string) => void;
  toggleMinimize: (chatId: string) => void;
  updateUnreadCount: (chatId: string, count: number) => void;
  markChatAsRead: (chatId: string) => void;
  isChatOpen: (chatId: string) => boolean;
}

const ChatBarContext = createContext<ChatBarContextType | undefined>(undefined);

const MAX_OPEN_CHATS = 5;

// Timestamps restored from localStorage are plain { seconds, nanoseconds } objects.
function millisOf(ts?: Timestamp | { seconds?: number }): number {
  if (!ts) return 0;
  if (typeof (ts as Timestamp).toMillis === 'function') return (ts as Timestamp).toMillis();
  return ((ts as { seconds?: number }).seconds || 0) * 1000;
}

export const ChatBarProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser } = useAuth();
  const [openChats, setOpenChats] = useState<ChatWindow[]>(() => {
    // Load from localStorage on init
    if (!currentUser?.uid) return [];
    try {
      const stored = localStorage.getItem(`chatBar-${currentUser.uid}`);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Keep chats minimized and reset unread counts on reload
        return parsed.map((chat: ChatWindow) => ({
          ...chat,
          isMinimized: true,
          unreadCount: 0
        }));
      }
    } catch (error) {
      console.error('Error loading chat bar state:', error);
    }
    return [];
  });

  // Save to localStorage whenever openChats changes
  useEffect(() => {
    if (!currentUser?.uid) return;
    try {
      localStorage.setItem(`chatBar-${currentUser.uid}`, JSON.stringify(openChats));
    } catch (error) {
      console.error('Error saving chat bar state:', error);
    }
  }, [openChats, currentUser?.uid]);

  // Unread counts for minimized chats. Keyed on the minimized chats only: depending on openChats
  // would re-subscribe (and re-read every message) each time a count changes.
  const minimizedKey = openChats
    .filter((c) => c.isMinimized && c.lastMessageTimestamp)
    .map((c) => `${c.chatId}@${millisOf(c.lastMessageTimestamp)}`)
    .join(',');
  const myEmail = currentUser?.email;

  useEffect(() => {
    if (!myEmail || !minimizedKey) return;

    const unsubscribes = minimizedKey.split(',').map((entry) => {
      const [chatId, since] = entry.split('@');
      const newer = query(
        collection(db, 'chats', chatId, 'messages'),
        where('timestamp', '>', Timestamp.fromMillis(Number(since))),
      );
      return onSnapshot(newer, (snapshot) => {
        const unread = snapshot.docs.filter((d) => d.data().sender !== myEmail).length;
        setOpenChats((prev) =>
          prev.some((c) => c.chatId === chatId && c.unreadCount !== unread)
            ? prev.map((c) => (c.chatId === chatId ? { ...c, unreadCount: unread } : c))
            : prev,
        );
      });
    });

    return () => unsubscribes.forEach((unsub) => unsub());
  }, [minimizedKey, myEmail]);

  const openChat = (chatId: string, chatData: any) => {
    // Check if chat is already open
    const existingChat = openChats.find(c => c.chatId === chatId);
    if (existingChat) {
      // If minimized, expand it
      if (existingChat.isMinimized) {
        toggleMinimize(chatId);
      }
      return;
    }

    // Check max limit
    if (openChats.length >= MAX_OPEN_CHATS) {
      // Remove the oldest minimized chat, or the first chat if none minimized
      const chatToRemove = openChats.find(c => c.isMinimized) || openChats[0];
      setOpenChats(prev => prev.filter(c => c.chatId !== chatToRemove.chatId));
    }

    // Add new chat
    const newChat: ChatWindow = {
      chatId,
      chatData,
      isMinimized: false,
      unreadCount: 0,
      lastMessageTimestamp: Timestamp.now()
    };

    setOpenChats(prev => [...prev, newChat]);
  };

  const closeChat = (chatId: string) => {
    setOpenChats(prev => prev.filter(c => c.chatId !== chatId));
  };

  const toggleMinimize = (chatId: string) => {
    setOpenChats(prev =>
      prev.map(c =>
        c.chatId === chatId
          ? { 
              ...c, 
              isMinimized: !c.isMinimized,
              unreadCount: c.isMinimized ? 0 : c.unreadCount, // Reset unread when expanding
              lastMessageTimestamp: !c.isMinimized ? Timestamp.now() : c.lastMessageTimestamp
            }
          : c
      )
    );
  };

  const updateUnreadCount = (chatId: string, count: number) => {
    setOpenChats(prev =>
      prev.map(c =>
        c.chatId === chatId ? { ...c, unreadCount: count } : c
      )
    );
  };

  const markChatAsRead = (chatId: string) => {
    setOpenChats(prev =>
      prev.map(c =>
        c.chatId === chatId 
          ? { ...c, unreadCount: 0, lastMessageTimestamp: Timestamp.now() } 
          : c
      )
    );
  };

  const isChatOpen = (chatId: string) => {
    return openChats.some(c => c.chatId === chatId);
  };

  return (
    <ChatBarContext.Provider
      value={{
        openChats,
        openChat,
        closeChat,
        toggleMinimize,
        updateUnreadCount,
        markChatAsRead,
        isChatOpen
      }}
    >
      {children}
    </ChatBarContext.Provider>
  );
};

export const useChatBar = () => {
  const context = useContext(ChatBarContext);
  if (context === undefined) {
    throw new Error('useChatBar must be used within a ChatBarProvider');
  }
  return context;
};

